import AddressDTO from "features/customer/dtos/AddressDTO";
import ContactDTO from "features/customer/dtos/ContactDTO";
import PageCard from "features/shared/components/card/PageCard";
import React from "react";
import { MailMethod } from "../enums/mailMethod";
import InfoItem from "features/shared/components/infoItem/InfoItem";
import AddressFormatter from "features/shared/components/formatters/AddressFormatter";
import { useTranslation } from "react-i18next";
import LinkButton from "features/shared/components/buttons/LinkButton";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import ContactsList from "features/customer/components/ContactsList";

const emailMethods: string[] = [MailMethod.EMAIL, MailMethod.B2B, MailMethod.B2C, MailMethod.CSV, MailMethod.EBILL, MailMethod.FA24, MailMethod.XLSX, MailMethod.ISDOC];
const addressMethods: string[] = [MailMethod.PAPIR];
const dataBoxMethods: string[] = [MailMethod.OdeslaniNaDS]; 

interface Props {
  contacts: ContactDTO[];
  mailMethods: UserEnumDTO[];
  address: AddressDTO;
  contactsCount?: number;
}

const SendingMethodsCard = ({ contacts, mailMethods, address, contactsCount }: Props) => {
  // DI
  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  // tanslations
  const tLabels = useTranslation("contracts\\contracts").t;
  const tTerms = useTranslation(["shared\\terms"]).t;
  const tTypes = useTranslation("customer\\enums\\contactType").t;

  // handlers
  const openContactsDialogHandler = (contacts: ContactDTO[]) => {
    const type = contacts[0].contactType;

    dialogService.openDialog(tTypes(type!), () =>
      <ContactsList contacts={contacts} />
    );
  }

  // sub-renders
  const renderMailMethods = () => {
    let methods = mailMethods.map((method, index) => <div key={index}>{method.name}</div>);
    
    if (methods.length === 0) {
      return <>{tTerms("Undefined")}</>
    } else {
      return <>{methods}</>;
    }
  }

  const renderAddressItem = () => {
    let includesAddressMethod = mailMethods.some(x => addressMethods.includes(x.code!))
    if (includesAddressMethod) {
      return (
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2">
          <InfoItem title={tLabels("sendingMethodsCard.labels.address")} value={renderFormattedAddress()} />
        </div>
      );
    }
  }
  
  const renderEmailItem = () => {
    let includesEmailMethod = mailMethods.some(x => emailMethods.includes(x.code!))
    if (includesEmailMethod) {
      return (
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2">
          <InfoItem 
            title={tLabels("sendingMethodsCard.labels.email")} 
            value={renderContacts(contacts.filter(x => x.contactType === "Email"))} 
          />
        </div>
      );
    }
  }

  const renderDataBoxItem = () => {
    let includesDataBoxMethod = mailMethods.some(x => dataBoxMethods.includes(x.code!))
    if (includesDataBoxMethod) {
      return (
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2">
          <InfoItem 
            title={tLabels("sendingMethodsCard.labels.dataBox")} 
            value={renderContacts(contacts.filter(x => x.contactType === "DataBox"))} 
          />
        </div>
      );
    }
  }

  const renderFormattedAddress = () => {
    return <AddressFormatter address={address} />
  }

  const renderContacts = (contacts: ContactDTO[]) => {
    let contactElems:JSX.Element[] = [];
    for (let index = 0; index < contacts.length; index++) {
      if (index === contactsCount) {
        break;
      }

      const item = contacts[index];
      let contactElem = <div key={index}>{item.value}</div>;
      contactElems.push(contactElem);
    }

    if (contactsCount && contacts.length > contactsCount) {
      let btn = (
        <LinkButton
          className="font-weight-normal"
          onClick={() => openContactsDialogHandler(contacts)}
        />
      );
      contactElems.push(btn);
    }

    return <>{contactElems}</>;
  }

  // render
  return (
    <PageCard title={tLabels("sendingMethodsCard.cardTitle")}>
      <div className="row no-gutters">
        <div className="col-sm-6 col-lg-4 px-sm-3 pb-2">
          <InfoItem title={tLabels("sendingMethodsCard.labels.mailMethods")} value={renderMailMethods()} />
        </div>
        {renderAddressItem()}
        {renderEmailItem()}
        {renderDataBoxItem()}
      </div>
    </PageCard>
  );
};

export default SendingMethodsCard;
